import React, { useContext, useEffect, useState } from 'react';
import { withFirebase, FirebaseContext } from '../../firebase';
import { AuthUserContext } from '../../session';
import { CChartLine } from '@coreui/react-chartjs';
import { registerables, Chart } from 'chart.js';
import axios from 'axios';

Chart.register(...registerables);

const GoogleFitBloodPressureChart = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const [systolicData, setSystolicData] = useState([]);
  const [diastolicData, setDiastolicData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [fitData, setFitData] = useState(null);

  const [error, setError] = useState(null);
  const fhirEndpoint = 'https://fhir.mieros.cloud/fhir/Observation';

  useEffect(() => {
    // Fetch the Google OAuth access token from local storage
    const userSession = JSON.parse(localStorage.getItem('userSession'));

    if (userSession && userSession.mieGoogleToken) {
      fetchGoogleFitData(userSession.mieGoogleToken);
    } else {
      setError('Google OAuth token not found. Please sign in again. User is not authenticated or Firebase is not initialized.');
    }
  }, [authUser, firebase]);

  const fetchGoogleFitData = async (accessToken0) => {
    try {
      const accessToken = localStorage.getItem('googleAccessToken');

      if (!accessToken) {
        setError('Google OAuth token not found. Please sign in again.');
        return;
      }

      const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      const requestBody = {
        aggregateBy: [
          {
            dataTypeName: "com.google.blood_pressure.systolic",
            dataSourceId: "derived:com.google.blood_pressure.systolic:com.google.android.gms:merge_blood_pressure_systolic"
          },
          {
            dataTypeName: "com.google.blood_pressure.diastolic",
            dataSourceId: "derived:com.google.blood_pressure.diastolic:com.google.android.gms:merge_blood_pressure_diastolic"
          }
        ],
        bucketByTime: { durationMillis: 86400000 }, // 1 day in milliseconds
        startTimeMillis: Date.now() - (30 * 24 * 60 * 60 * 1000), // Last 30 days
        endTimeMillis: Date.now(),
      };

      const response = await fetch('https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response: ", errorData);
        throw new Error(errorData.error.message);
      }

      const data = await response.json();

      // Process the data and update the state
      const formattedLabels = data.bucket.map(item => formatDate(item.startTimeMillis));
      const formattedSystolic = data.bucket.map(item => item.dataset[0].point[0]?.value[0].fpVal || 0); // Systolic values
      const formattedDiastolic = data.bucket.map(item => item.dataset[1].point[0]?.value[0].fpVal || 0); // Diastolic values

      setLabels(formattedLabels);
      setSystolicData(formattedSystolic);
      setDiastolicData(formattedDiastolic);
      setFitData(data);

    } catch (err) {
      setError('Error fetching Google Fit data');
      console.error(err);
    }
  };

  const formatDate = (dateMillis) => {
    const date = new Date(parseInt(dateMillis)); // Ensure it's converted to a number
    if (isNaN(date.getTime())) {
      return 'Invalid Date';  // Handle any invalid dates
    }
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const chartData = {
    labels: labels.length > 0 ? labels : ["No Data"], // Ensure no empty labels
    datasets: [
      {
        label: 'Systolic',
        data: systolicData.length > 0 ? systolicData : [0], // Ensure no empty data,
        fill: false,
        backgroundColor: 'rgba(75,192,192,0.4)',
        borderColor: 'rgba(75,192,192,1)',
      },
      {
        label: 'Diastolic',
        data: diastolicData.length > 0 ? diastolicData : [0], // Ensure no empty data,
        fill: false,
        backgroundColor: 'rgba(255,99,132,0.2)',
        borderColor: 'rgba(255,99,132,1)',
      },
    ],
  };

  const options = {
    scales: {
      x: {
        type: 'category', // Change to 'category' since we're using string labels
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div>
      <h2>Blood Pressure Last Month</h2>
      {error && <p>Error: {error}</p>}
      <CChartLine data={chartData} options={options} />
    </div>
  );
};

export default withFirebase(GoogleFitBloodPressureChart);
